/**
 * useZohoLeadGate Hook
 * Checks if the current user is registered as a lead in Zoho CRM
 * If not, requires them to complete the lead form before accessing portal
 */
import { useState, useEffect, useCallback } from 'react';

const API_URL = window.location.origin;

export const useZohoLeadGate = (userEmail, userName) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [zohoLeadId, setZohoLeadId] = useState(null);
  const [error, setError] = useState(null);

  // Check if user exists as a lead in Zoho
  const checkLeadStatus = useCallback(async () => {
    if (!userEmail) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // First check localStorage for cached verification
      const cachedData = localStorage.getItem(`zoho_verified_${userEmail}`);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        // If profile_completed is true, it's permanent - no need for time check
        if (parsed.profile_completed) {
          setIsVerified(true);
          setZohoLeadId(parsed.zoho_lead_id);
          setIsLoading(false);
          return;
        }
        // Otherwise, cache valid for 24 hours
        if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          setIsVerified(true);
          setZohoLeadId(parsed.zoho_lead_id);
          setIsLoading(false);
          return;
        }
      }

      // Call API to check Zoho (now checks local DB first)
      const response = await fetch(
        `${API_URL}/api/zoho/check-lead?email=${encodeURIComponent(userEmail)}`
      );
      const data = await response.json();

      if (data.exists) {
        // User is already a lead in Zoho or has completed profile
        setIsVerified(true);
        setZohoLeadId(data.zoho_lead_id);
        setShowLeadForm(false);
        
        // Cache the verification permanently if from local DB
        localStorage.setItem(`zoho_verified_${userEmail}`, JSON.stringify({
          zoho_lead_id: data.zoho_lead_id,
          profile_completed: true,  // Mark as permanently completed
          timestamp: Date.now()
        }));
      } else {
        // User needs to complete lead form
        setIsVerified(false);
        setShowLeadForm(true);
      }
    } catch (err) {
      console.error('Error checking Zoho lead status:', err);
      setError(err.message);
      // On error, allow access but log the issue
      setIsVerified(true);
    } finally {
      setIsLoading(false);
    }
  }, [userEmail]);

  // Handle successful lead form submission
  const onLeadFormComplete = useCallback((leadId) => {
    setZohoLeadId(leadId);
    setIsVerified(true);
    setShowLeadForm(false);
    
    // Cache the verification permanently
    if (userEmail) {
      localStorage.setItem(`zoho_verified_${userEmail}`, JSON.stringify({
        zoho_lead_id: leadId,
        profile_completed: true,  // Mark as permanently completed
        timestamp: Date.now()
      }));
    }
  }, [userEmail]);

  // Reset and re-check
  const resetVerification = useCallback(() => {
    if (userEmail) {
      localStorage.removeItem(`zoho_verified_${userEmail}`);
    }
    setIsVerified(false);
    setZohoLeadId(null);
    checkLeadStatus();
  }, [userEmail, checkLeadStatus]);

  useEffect(() => {
    checkLeadStatus();
  }, [checkLeadStatus]);

  return {
    isVerified,
    isLoading,
    showLeadForm,
    setShowLeadForm,
    zohoLeadId,
    error,
    onLeadFormComplete,
    resetVerification
  };
};

export default useZohoLeadGate;
